import { debounce } from "lodash";

// 获取当前节点所在的 window 对象
const getWindow = (node: Node): Window => {
  if (node.toString() !== '[object Window]') {
    const ownerDocument = node.ownerDocument;
    return ownerDocument ? ownerDocument.defaultView || window : window;
  }
  return node as unknown as Window;
};

// 获取包含块（containing block）
const getContainingBlock = (node: HTMLElement): HTMLElement => {
  let currentNode: HTMLElement | null = node;

  while (
    currentNode &&
    currentNode !== document.documentElement &&
    currentNode !== document.body
  ) {
    const css = getComputedStyle(currentNode);

    if (
      css.transform !== 'none' ||
      css.perspective !== 'none' ||
      (css.willChange && css.willChange !== 'auto') ||
      css.filter !== 'none' ||
      css.contain === 'paint'
    ) {
      return currentNode;
    }
    currentNode = currentNode.parentElement;
  }

  return document.documentElement;
};

// 获取文档的滚动偏移量
const getScrollOffset = (win: Window) => {
  return {
    scrollX: win.pageXOffset !== undefined ? win.pageXOffset : win.scrollX,
    scrollY: win.pageYOffset !== undefined ? win.pageYOffset : win.scrollY
  };
};

// 获取锚元素的位置
export const getAnchorPosition = (anchorOrSelector: string | HTMLElement) => {
  // 如果传入的是字符串（选择器），则使用 querySelector 查找元素
  let anchor: HTMLElement | null;
  if (typeof anchorOrSelector === 'string') {
    anchor = document.querySelector(anchorOrSelector) as HTMLElement | null;
  } else {
    // 如果传入的是具体的 DOM 元素，直接使用
    anchor = anchorOrSelector;
  }

  // 检查 anchor 是否存在
  if (!anchor) {
    throw new Error(`Element with selector "${anchorOrSelector}" not found`);
  }

  // 获取锚元素的 window 对象
  const win = getWindow(anchor);

  // 获取锚元素的 offsetParent 或 containing block
  let parent: HTMLElement | null = anchor.offsetParent as HTMLElement;
  if (!parent || getComputedStyle(anchor).position === 'fixed') {
    parent = getContainingBlock(anchor);
  }

  // 获取锚元素和父元素的矩形信息
  const anchorRect = anchor.getBoundingClientRect();
  const parentRect = parent.getBoundingClientRect();

  // 获取滚动偏移量
  const { scrollX, scrollY } = getScrollOffset(win);

  // 计算锚元素相对于 offsetParent 的位置信息
  // 修正父元素的 scroll 偏移量
  const parentScrollTop = parent.scrollTop || 0;
  const parentScrollLeft = parent.scrollLeft || 0;

  // 计算锚元素的绝对位置
  const position = {
    top: anchorRect.top - parentRect.top + scrollY + parentScrollTop,
    left: anchorRect.left - parentRect.left + scrollX + parentScrollLeft,
    width: anchorRect.width,
    height: anchorRect.height,
    x: anchorRect.left,
    y: anchorRect.top
  };

  return {
    anchor,
    parent,
    position
  };
};


export interface ElementInfo {
  element: Element;
  position: { x: number; y: number };
  size: { width: number; height: number };
  rect: DOMRect;
}

/** 镂空区域调整配置 */
export interface HoleAdjustment {
  /** X轴偏移量，正值向右，负值向左 */
  offsetX?: number;
  /** Y轴偏移量，正值向下，负值向上 */
  offsetY?: number;
  /** 宽度调整量，正值增加宽度，负值减少宽度 */
  paddingWidth?: number;
  /** 高度调整量，正值增加高度，负值减少高度 */
  paddingHeight?: number;
  /** 圆角半径 */
  radius?: number;
  /** 是否使用固定尺寸（忽略原始元素尺寸） */
  fixedSize?: {
    width: number;
    height: number;
  };
}

export interface SelectorObject {
  selector: string;
  info?: ElementInfo | null;
  handleClick?: () => void;
  handleMouseEnter?: () => void;
  handleMounseLeave?: () => void;
  popverTips?: boolean;
  placement?: string;
  name?: string;
  className?: string;
  onlyGuide?: boolean;
  pointerEvents?: string[];
  /** 镂空区域调整配置 */
  holeAdjustment?: HoleAdjustment;
}

/**
 * 等待元素动画完成
 */
const waitForAnimationEnd = (element: Element): Promise<void> => {
  return new Promise((resolve) => {
    // 检查是否有正在进行的动画或过渡
    const computedStyle = getComputedStyle(element);
    const animationDuration = parseFloat(computedStyle.animationDuration) || 0;
    const transitionDuration = parseFloat(computedStyle.transitionDuration) || 0;
    
    // 如果没有动画或过渡，直接resolve
    if (animationDuration === 0 && transitionDuration === 0) {
      resolve();
      return;
    }

    let animationEndHandler: (() => void) | null = null;
    let transitionEndHandler: (() => void) | null = null;
    let timeoutId: NodeJS.Timeout | null = null;
    
    let pendingEvents = 0;
    
    // 增加待处理事件计数
    if (animationDuration > 0) pendingEvents++;
    if (transitionDuration > 0) pendingEvents++;
    
    const cleanup = () => {
      if (animationEndHandler) {
        element.removeEventListener('animationend', animationEndHandler);
      }
      if (transitionEndHandler) {
        element.removeEventListener('transitionend', transitionEndHandler);
      }
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    
    const checkComplete = () => {
      pendingEvents--;
      if (pendingEvents <= 0) {
        cleanup();
        resolve();
      }
    };
    
    // 监听动画结束事件
    if (animationDuration > 0) {
      animationEndHandler = () => checkComplete();
      element.addEventListener('animationend', animationEndHandler, { once: true });
    }
    
    // 监听过渡结束事件
    if (transitionDuration > 0) {
      transitionEndHandler = () => checkComplete();
      element.addEventListener('transitionend', transitionEndHandler, { once: true });
    }
    
    // 设置超时机制，防止事件未触发导致永久等待
    const maxDuration = Math.max(animationDuration, transitionDuration) * 1000 + 100; // 转换为毫秒并加100ms缓冲
    timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, maxDuration);
  });
};

/**
 * 多次采样获取稳定的元素位置
 */
const getStablePosition = async (element: Element, maxAttempts: number = 3): Promise<DOMRect> => {
  const positions: DOMRect[] = [];
  
  for (let i = 0; i < maxAttempts; i++) {
    await new Promise(resolve => requestAnimationFrame(resolve));
    positions.push(element.getBoundingClientRect());
    
    // 如果不是最后一次采样，等待一小段时间
    if (i < maxAttempts - 1) {
      await new Promise(resolve => setTimeout(resolve, 16)); // 约1帧的时间
    }
  }
  
  // 检查位置是否稳定（连续两次位置差异小于1px）
  const isStable = positions.length >= 2 && 
    Math.abs(positions[positions.length - 1].x - positions[positions.length - 2].x) < 1 &&
    Math.abs(positions[positions.length - 1].y - positions[positions.length - 2].y) < 1;
  
  if (!isStable && maxAttempts < 5) {
    // 如果位置不稳定，增加采样次数重试
    return getStablePosition(element, maxAttempts + 1);
  }
  
  // 返回最后一次采样的位置
  return positions[positions.length - 1];
};

/**
 * 等待页面布局稳定
 */
const waitForLayoutStable = (): Promise<void> => {
  return new Promise((resolve) => {
    let rafCount = 0;
    const checkStable = () => {
      rafCount++;
      if (rafCount >= 2) { // 等待至少2帧
        resolve();
      } else {
        requestAnimationFrame(checkStable);
      }
    };
    requestAnimationFrame(checkStable);
  });
};

/**
 * 检测元素是否为Popover类型
 */
export const isPopoverElement = (element: Element): boolean => {
  const classList = element.classList;
  const tagName = element.tagName.toLowerCase();
  
  // 检查常见的popover相关类名和属性
  return classList.contains('ant-popover') ||
         classList.contains('popover') ||
         classList.contains('tooltip') ||
         classList.contains('dropdown') ||
         element.getAttribute('role') === 'tooltip' ||
         element.getAttribute('role') === 'popup' ||
         tagName === 'popover' ||
         // 检查是否在popover容器内
         !!element.closest('.ant-popover, .popover, .tooltip, .dropdown, [role="tooltip"], [role="popup"]');
};

/**
 * 等待Popover元素稳定显示
 */
const waitForPopoverStable = async (element: Element): Promise<void> => {
  if (!isPopoverElement(element)) {
    return;
  }
  
  return new Promise((resolve) => {
    let checkCount = 0;
    const maxChecks = 20; // 最多检查20次
    let lastRect: DOMRect | null = null;
    
    const checkStability = () => {
      const currentRect = element.getBoundingClientRect();
      
      // 检查元素是否可见
      if (currentRect.width === 0 || currentRect.height === 0) {
        checkCount++;
        if (checkCount < maxChecks) {
          setTimeout(checkStability, 50);
          return;
        }
      }
      
      // 检查位置是否稳定
      if (lastRect) {
        const isStable = 
          Math.abs(currentRect.x - lastRect.x) < 1 &&
          Math.abs(currentRect.y - lastRect.y) < 1 &&
          Math.abs(currentRect.width - lastRect.width) < 1 &&
          Math.abs(currentRect.height - lastRect.height) < 1;
          
        if (isStable || checkCount >= maxChecks) {
          resolve();
          return;
        }
      }
      
      lastRect = currentRect;
      checkCount++;
      
      if (checkCount < maxChecks) {
        setTimeout(checkStability, 50);
      } else {
        resolve();
      }
    };
    
    // 立即开始检查
    checkStability();
  });
};

/**
 * 获取Popover元素的实际渲染容器
 */
const getPopoverContainer = (element: Element): Element => {
  // 检查是否在portal容器中
  const portalContainer = element.closest('.ant-popover-content, .popover-content, .tooltip-content') || element;
  
  // 向上查找真正的popover根容器
  const popoverRoot = portalContainer.closest('.ant-popover, .popover, .tooltip, .dropdown') || portalContainer;
  
  return popoverRoot;
};

/**
 * 获取稳定的元素位置信息（增强版，特别处理Popover）
 */
const getStableElementInfo = async (element: Element): Promise<ElementInfo> => {
  // 1. 如果是Popover元素，特殊处理
  if (isPopoverElement(element)) {
    console.log('🎯 检测到Popover元素，使用特殊处理逻辑');
    
    // 获取实际的popover容器
    const popoverContainer = getPopoverContainer(element);
    
    // 等待popover稳定显示
    await waitForPopoverStable(popoverContainer);
    
    // 额外等待一帧确保渲染完成
    await new Promise(resolve => requestAnimationFrame(resolve));
  }
  
  // 2. 等待动画完成
  await waitForAnimationEnd(element);
  
  // 3. 等待页面布局稳定
  await waitForLayoutStable();
  
  // 4. 多次采样获取稳定位置
  const rect = await getStablePosition(element);
  
  const elementInfo: ElementInfo = {
    element,
    position: { x: rect.x, y: rect.y },
    size: { width: rect.width, height: rect.height },
    rect: rect
  };
  
  return elementInfo;
};

export function observeElements(selectorsObj: SelectorObject[], requiredCount: number = 1): Promise<SelectorObject[]> {
  return new Promise(async (resolve) => {
    const observedElements = new Set<Element>();
    const processedElements = new Set<Element>();
    
    const observer = new IntersectionObserver(async (entries) => {
      const visibleElements: ElementInfo[] = [];

      for (const entry of entries) {
        if (entry.isIntersecting && !processedElements.has(entry.target)) {
          processedElements.add(entry.target);
          
          try {
            // 获取稳定的元素位置信息
            const elementInfo = await getStableElementInfo(entry.target);
            visibleElements.push(elementInfo);
            observer.unobserve(entry.target);

            // 更新传入的对象
            const matchingObj = selectorsObj.find(obj => entry.target.matches(obj.selector));
            if (matchingObj) {
              matchingObj.info = elementInfo;
            }
          } catch (error) {
            console.warn('获取元素位置信息失败:', error);
            // 如果获取失败，使用原有逻辑作为备选方案
            const rect = entry.boundingClientRect;
            const elementInfo: ElementInfo = {
              element: entry.target,
              position: { x: rect.x, y: rect.y },
              size: { width: rect.width, height: rect.height },
              rect: rect
            };
            visibleElements.push(elementInfo);
            observer.unobserve(entry.target);

            const matchingObj = selectorsObj.find(obj => entry.target.matches(obj.selector));
            if (matchingObj) {
              matchingObj.info = elementInfo;
            }
          }
        }
      }

      if (visibleElements.length > 0) {
        visibleElements.forEach(item => observedElements.add(item.element));
        if (observedElements.size >= requiredCount) {
          observer.disconnect();
          mutationObserver.disconnect(); // 停止监测 DOM 变化
          resolve(selectorsObj); // 返回更新后的对象数组
        }
      }
    });

    const checkAndObserve = debounce(() => {
      selectorsObj.forEach(({ selector }) => {
        const selectedElements = document.querySelectorAll(selector);
        selectedElements.forEach(element => {
          if (!observedElements.has(element) && !processedElements.has(element)) {
            observedElements.add(element);
            observer.observe(element);
          }
        });
      });
    }, 100); // 设定防抖时间

    const mutationObserver = new MutationObserver(checkAndObserve);
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    // 初始检查
    checkAndObserve();
  });
}

/**
 * 检测元素是否有正在进行的动画或过渡
 */
export const hasActiveAnimation = (element: Element): boolean => {
  const computedStyle = getComputedStyle(element);
  const animationDuration = parseFloat(computedStyle.animationDuration) || 0;
  const transitionDuration = parseFloat(computedStyle.transitionDuration) || 0;
  
  return animationDuration > 0 || transitionDuration > 0;
};

/**
 * 获取元素动画的总时长（包括动画和过渡）
 */
export const getAnimationDuration = (element: Element): number => {
  const computedStyle = getComputedStyle(element);
  const animationDuration = parseFloat(computedStyle.animationDuration) || 0;
  const transitionDuration = parseFloat(computedStyle.transitionDuration) || 0;
  
  return Math.max(animationDuration, transitionDuration) * 1000; // 转换为毫秒
};

